import type { InteractionAction, InteractionStepProps } from '../HistoryStep';
import type { RetryState } from './ContactDetailPageCore';
import type { HistoryItem } from './types';
import { InteractionStep } from '../HistoryStep';

/**
 * Everything a history entry can DO — the same callbacks and per-entry states whether the entry sits in the
 * interact tab's feed or opens from a card on the sequence canvas, so both render the identical, fully
 * wired entry (Respond, retry an attachment, transcribe a call, mark a meeting booked).
 */
export type InteractionHistoryActions = {
  currentUserId: string;
  onClickCadence?: (cadenceId: string) => void;
  onClickToRespond?: InteractionStepProps['onClickToRespond'];
  onMarkMeetingBooked: (interactionPublicId: string) => void;
  isMarkingMeetingBooked?: boolean;
  onRetryAttachment?: (interactionId: string) => void;
  retryStates?: Record<string, RetryState>;
  retryErrors?: Record<string, string | null>;
  onRequestTranscription?: (interactionId: string) => void;
  transcriptionRequestStates?: Record<string, 'idle' | 'pending' | 'success' | 'error'>;
};

/** The id an entry is keyed (and retried / transcribed) by — the public id when there is one. */
export function interactionHistoryId(entry: HistoryItem): string {
  return entry.interaction.odooInteractionId ?? String(entry.interaction.id);
}

/** The interaction's action — the stored action when present, else its type. */
export function interactionHistoryAction(entry: {
  interaction: Pick<HistoryItem['interaction'], 'odooInteractionAction' | 'interactionType'>;
}): InteractionAction {
  // The server's closed action set, typed as a plain string on this layer (no pg-gen import here).
  return (entry.interaction.odooInteractionAction ?? entry.interaction.interactionType) as InteractionAction;
}

interface Props {
  entry: HistoryItem;
  actions: InteractionHistoryActions;
  /** The previous entry's date, for the feed's day grouping (omit for a standalone entry). */
  prevDate?: Date | null;
  /** The last entry in its list (always shows its time). */
  isLast?: boolean;
}

/** One entry of a contact's history — a message, a reply, a call, a document view — fully wired. */
export function InteractionHistoryItem({ entry, actions, prevDate, isLast }: Props) {
  const interaction = entry.interaction;
  const interactionId = interactionHistoryId(entry);
  return (
    <InteractionStep
      currentUserId={actions.currentUserId}
      contactFirstName={entry.contact?.primaryFields?.firstName?.data}
      contactLastName={entry.contact?.primaryFields?.lastName?.data}
      interactionId={interactionId}
      interactionUserId={interaction.userId}
      interactionAction={interactionHistoryAction(entry)}
      interactionDate={interaction.createdAt}
      interactionUpdatedOn={interaction.updatedOn ?? interaction.createdAt}
      interactionSubject={interaction.subject}
      interactionBody={interaction.body}
      interactionFromNumber={interaction.from}
      interactionToNumber={interaction.to}
      interactionDataUrl={interaction.dataUrl}
      interactionDataUrlType={interaction.dataUrlType}
      actorName={entry.interactionUser?.name}
      actorEmail={entry.interactionUser?.email ?? ''}
      documentId={entry.documentView?.id}
      documentTitle={entry.documentView?.title}
      documentOgImageUrl={entry.documentView?.ogImageUrl}
      cadenceName={entry.cadence?.name}
      cadenceId={entry.cadence?.id}
      onClickCadence={actions.onClickCadence}
      prevInteractionDate={prevDate}
      isLast={isLast}
      onClickToRespond={actions.onClickToRespond}
      onMarkMeetingBooked={actions.onMarkMeetingBooked}
      isMarkingMeetingBooked={actions.isMarkingMeetingBooked}
      onRetryAttachment={actions.onRetryAttachment}
      retryState={actions.retryStates?.[interactionId] ?? 'idle'}
      retryError={actions.retryErrors?.[interactionId]}
      transcription={interaction.transcription}
      transcriptionModel={interaction.transcription_model}
      transcriptionStatus={interaction.transcription_status}
      transcriptionError={interaction.transcription_error}
      onRequestTranscription={actions.onRequestTranscription}
      transcriptionRequestState={actions.transcriptionRequestStates?.[interactionId] ?? 'idle'}
      meetingBookedFromThis={interaction.meetingBookedFromThis}
    />
  );
}
